import Divider from '@material-ui/core/Divider';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import Spin from 'components/Spin';
import Truncate from 'components/Truncate';
import React, { memo, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useObservable } from 'react-use-observable';
import { dateMomentFormat } from 'v2/formatters/date';
import chatService from 'v2/services/chat';

import { useStyles } from './styles';

interface IProps {
  isOpen: boolean;
  isFirstOpen: boolean;
  onClose: () => void;
  anchorEl: null | HTMLElement;
  unreadMessagesCount: number;
}

const MessagesList = memo(({ isOpen, onClose, anchorEl, unreadMessagesCount, isFirstOpen }: IProps) => {
  const classes = useStyles();

  const [messages] = useObservable(() => chatService.getMessages(), []);
  const [isLoading] = useObservable(() => chatService.getLoadingMessages(), []);

  const shouldLoadMessages = useMemo(() => {
    if (!isOpen) {
      return false;
    }

    if (isFirstOpen || unreadMessagesCount > 0) {
      return true;
    }

    return false;
  }, [isFirstOpen, isOpen, unreadMessagesCount]);

  const renderMessages = useMemo(() => {
    if (isLoading) {
      return (
        <div className={classes.loading}>
          <Spin />
        </div>
      );
    }

    if (!messages?.length) {
      return (
        <div className={classes.emptyMessage}>
          <Typography align='center' variant='body1'>
            Nenhuma mensagem disponível
          </Typography>
        </div>
      );
    }

    return messages?.map((chat) => {
      return (
        <Link key={chat.id} to={'/producer/messages/' + chat.id} onClick={onClose}>
          <div className={`${classes.chatItem} ${chat.not_read_count > 0 ? '--not-read' : ''}`}>
            <div
              className={`${classes.chatImage} ${chat.not_read_count > 0 ? '--not-read' : ''}`}
              style={{
                backgroundImage: `url(${chat.outer.avatar ? chat.outer.avatar : '/img/avatar-default.png'})`,
              }}
            >
              <span></span>
              <div
                className={`${classes.badgeAvatar} ${chat.outer.qualification ? '--hasBadge' : ''}`}
                style={{
                  backgroundImage: `url(${
                    chat.outer.qualification
                      ? `/img/badges/${chat.outer.qualification.badge_elite}` + (chat.outer.qualification.has_plus ? '-plus' : '') + '.svg'
                      : ''
                  })`,
                }}
              ></div>
            </div>
            <div>
              <div className={classes.chatTitle}>
                <Truncate count={25} final='...'>
                  {chat.outer.name}
                </Truncate>
              </div>
              <div className={classes.chatText}>
                <Truncate count={30} final='...'>
                  {chat.last.content}
                </Truncate>
              </div>
              <div className={classes.chatDate}>{dateMomentFormat(chat.last.created_at)}</div>
            </div>
          </div>
        </Link>
      );
    });
  }, [classes, isLoading, messages, onClose]);

  useEffect(() => {
    if (shouldLoadMessages) {
      chatService.loadMessages(4);
    }
  }, [shouldLoadMessages]);

  return (
    <Popover
      id='chat-list-menu'
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      classes={{ paper: classes.menu }}
    >
      <div className={classes.chatList}>
        <Typography align='center' variant='subtitle1'>
          Mensagens
        </Typography>
        <Divider />

        {renderMessages}
      </div>

      <Divider />

      <Link to={'/producer/messages'} onClick={onClose}>
        <span className={classes.viewMore}>VER TODAS</span>
      </Link>
    </Popover>
  );
});

export default MessagesList;
